import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaTimes, FaInbox, FaChevronLeft } from 'react-icons/fa';

export { auditStatus, bookingStatus } from './status';

export const Page = ({ narrow, children }) => (
  <div className={`page${narrow ? ' narrow' : ''}`}>{children}</div>
);

export const PageHeader = ({ title, subtitle, back, actions }) => (
  <div className="page-header">
    <div className="titles">
      {back && (
        <Link to={back.to} className="breadcrumb">
          <FaChevronLeft size={10} /> {back.label}
        </Link>
      )}
      <h1>{title}</h1>
      {subtitle && <p className="subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="actions">{actions}</div>}
  </div>
);

export const Card = ({ title, hint, action, children, footer, flush, className = '' }) => (
  <section className={`card ${className}`}>
    {(title || action) && (
      <div className="card-head">
        <div>
          {title && <h2>{title}</h2>}
          {hint && <p className="hint">{hint}</p>}
        </div>
        {action}
      </div>
    )}
    {flush ? children : <div className="card-body">{children}</div>}
    {footer && <div className="card-foot">{footer}</div>}
  </section>
);

export const SearchInput = ({ value, onChange, placeholder = 'Search' }) => (
  <div className="search">
    <FaSearch />
    <input
      className="input"
      type="search"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export const Badge = ({ tone = 'gray', children }) => (
  <span className={`badge badge-${tone}`}>{children}</span>
);

export const Switch = ({ on, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!on}
    aria-label={label}
    className={`switch${on ? ' on' : ''}`}
    onClick={onClick}
  />
);

export const Modal = ({ open, title, subtitle, onClose, children, footer, wide, onSubmit }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  const Body = onSubmit ? 'form' : 'div';
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <Body
        className={`modal-panel${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        onSubmit={onSubmit ? (e) => { e.preventDefault(); onSubmit(e); } : undefined}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </Body>
    </div>
  );
};

export const Field = ({ label, help, error, children, full }) => (
  <div className={`field${full ? ' full' : ''}`}>
    {label && <label>{label}</label>}
    {children}
    {help && !error && <span className="help">{help}</span>}
    {error && <span className="error-text">{error}</span>}
  </div>
);

export const CheckGrid = ({ options, value, onChange }) => (
  <div className="check-grid">
    {options.map((option) => {
      const on = value.includes(option);
      return (
        <label key={option} className={`check${on ? ' on' : ''}`}>
          <input
            type="checkbox"
            checked={on}
            onChange={() => onChange(on ? value.filter((v) => v !== option) : [...value, option])}
          />
          {option}
        </label>
      );
    })}
  </div>
);

export const EmptyRow = ({ cols, children = 'Nothing here yet.' }) => (
  <tr>
    <td colSpan={cols} className="empty">
      <FaInbox />
      {children}
    </td>
  </tr>
);

export const Loading = () => (
  <div className="loading-block"><div className="spinner" /></div>
);

export const Alert = ({ tone = 'error', children }) =>
  children ? <div className={`alert alert-${tone}`}>{children}</div> : null;

export const initials = (name = '') =>
  name.replace(/[^\p{L}\s]/gu, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

export const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatMoney = (value) =>
  value === undefined || value === null || value === ''
    ? '—'
    : `₹${Number(value).toLocaleString('en-IN')}`;
